import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { NbIconLibraries, NbSidebarService } from '@nebular/theme';
import { NbMenuItem, NbMenuService } from '@nebular/theme';
import { ApiService } from 'src/app/services/api.service';
import { ApiUrl } from 'src/app/services/apiUrl';
import { TranslateService } from '@ngx-translate/core';
import { buildVersion } from 'src/environments/version';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
})
export class LayoutComponent implements OnInit, OnDestroy {
  isSidebarOpened = true;
  role: any = localStorage.getItem('role');
  show2FAWarning: boolean = false;
  adminCommit: string = buildVersion.commit;
  apiCommit: string = '';
  apiBuildDate: string = '';
  private destroy$ = new Subject<void>();

  constructor(
    private sidebarService: NbSidebarService,
    private route: Router,
    private menuService: NbMenuService,
    private router: Router,
    private iconLibraries: NbIconLibraries,
    private apiService: ApiService,
    private http: HttpClient,
    private translateService: TranslateService,
  ) {
    this.iconLibraries.registerFontPack('font-awesome', { packClass: 'fa' });

    iconLibraries.registerSvgPack('my-icon', {
      dashboard:
        '<img src="assets/images/stag icons/dashboard.svg" width="15px">',
      user: '',
      logout: '',
    });
  }

  menu: NbMenuItem[] = [];

  get currentLang(): string {
    return this.translateService.currentLang || localStorage.getItem('hit2open_lang') || 'en';
  }

  setLanguage(lang: string): void {
    localStorage.setItem('hit2open_lang', lang);
    this.translateService.use(lang).subscribe(() => {
      this.buildMenu();
    });
  }

  ngOnInit() {
    const savedLang = localStorage.getItem('hit2open_lang') || 'en';
    this.translateService.use(savedLang).subscribe(() => {
      this.buildMenu();
    });
    this.translateService.onTranslationChange.subscribe(() => {
      this.buildMenu();
    });
    this.load2FAStatus();
    this.loadApiVersion();
    this.menuService.onItemClick()
      .pipe(
        filter(({ tag }) => tag === 'leftSideBar'),
        takeUntil(this.destroy$)
      )
      .subscribe(({ item }) => {
        if (item.link) {
          this.router.navigateByUrl(item.link);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  buildMenu() {
    const t = (key: string) => this.translateService.instant(key);
    this.role = localStorage.getItem('role');

    const allItems: NbMenuItem[] = [
      {
        title: t('MENU.DASHBOARD'),
        link: '/dashboard',
        icon: 'grid-outline',
        data: { key: 'DASHBOARD' },
      },
      {
        title: t('MENU.ADMIN'),
        link: '/adminlist',
        icon: 'person-outline',
        data: { key: 'ADMIN' },
      },
      {
        title: t('MENU.USERS'),
        link: '/users',
        icon: 'people-outline',
        data: { key: 'USERS' },
      },
      {
        title: t('MENU.STORES'),
        link: '/stores',
        icon: 'shopping-bag-outline',
        data: { key: 'STORES' },
      },
      {
        title: t('MENU.STORE_DOOR_STATUS'),
        link: '/doorstatus',
        icon: 'shopping-bag-outline',
        data: { key: 'STORE_DOOR_STATUS' },
      },
      {
        title: t('MENU.NFC_MONITORING'),
        link: '/nfc-monitoring',
        icon: 'activity-outline',
        data: { key: 'NFC_MONITORING' },
      },
      {
        title: t('MENU.MOBILE_DIAGNOSTICS'),
        link: '/mobile-diagnostics',
        icon: 'smartphone-outline',
        data: { key: 'MOBILE_DIAGNOSTICS' },
      },
      {
        title: t('MENU.GRAPH'),
        link: '/graph',
        icon: 'pie-chart-outline',
        data: { key: 'GRAPH' },
      },
      {
        title: t('MENU.USERS_VISIT'),
        link: '/uservisit',
        icon: 'people-outline',
        data: { key: 'USERS_VISIT' },
      },
      {
        title: t('MENU.ONBOARDING_IMAGES'),
        link: '/onboardingimages',
        icon: 'folder-outline',
        data: { key: 'ONBOARDING_IMAGES' },
      },
      {
        title: t('MENU.TERMS'),
        link: '/termsandconditions',
        icon: 'file-text-outline',
        data: { key: 'TERMS' },
      },
      {
        title: t('MENU.INFO_PAGE'),
        link: '/infopage',
        icon: 'info-outline',
        data: { key: 'INFO_PAGE' },
      },
      {
        title: t('MENU.QR_LANDING'),
        link: '/qrlandingpage',
        icon: 'smartphone-outline',
        data: { key: 'QR_LANDING' },
      },
      {
        title: t('MENU.NOTIFICATION'),
        link: '/notification',
        icon: 'bell-outline',
        data: { key: 'NOTIFICATION' },
      },
      {
        title: t('MENU.HOLIDAYS'),
        link: '/holidays',
        icon: 'calendar-outline',
        data: { key: 'HOLIDAYS' },
      },
      {
        title: t('MENU.CARD_ACCESS'),
        link: '/carduidmanager',
        icon: 'credit-card-outline',
        data: { key: 'CARD_ACCESS' },
      },
      {
        title: t('MENU.BLOCKED_CPR'),
        link: '/blocked-cpr',
        icon: 'shield-outline',
        data: { key: 'BLOCKED_CPR' },
      },
      {
        title: t('MENU.SECURITY_SETTINGS'),
        link: '/security-settings',
        icon: 'lock-outline',
        data: { key: 'SECURITY_SETTINGS' },
      },
      {
        title: t('MENU.DEVELOPER_CLIENTS'),
        link: '/developer-clients',
        icon: 'code-outline',
        data: { key: 'DEVELOPER_CLIENTS' },
      },
      {
        title: t('MENU.SSO_APPLICATIONS'),
        link: '/sso-applications',
        icon: 'key-outline',
        data: { key: 'SSO_APPLICATIONS' },
      },
      {
        title: t('MENU.SUPPORT_CENTER'),
        link: '/support',
        icon: 'question-mark-circle-outline',
        data: { key: 'SUPPORT_CENTER' },
      },
      {
        title: t('MENU.DEPOSIT_FEE_CATALOG'),
        link: '/deposit-fee-catalog',
        icon: 'archive-outline',
        data: { key: 'DEPOSIT_FEE_CATALOG' },
      },
      {
        title: t('MENU.GLOBAL_CATALOG'),
        link: '/global-catalog',
        icon: 'globe-outline',
        data: { key: 'GLOBAL_CATALOG' },
      },
      {
        title: t('MENU.INVOICE_IMPORTS_UNASSIGNED'),
        link: '/invoice-imports/unassigned',
        icon: 'email-outline',
        data: { key: 'INVOICE_IMPORTS_UNASSIGNED' },
      },
      {
        title: t('MENU.WHOLESALER_DEFINITIONS'),
        link: '/admin/wholesaler-definitions',
        icon: 'book-outline',
        data: { key: 'WHOLESALER_DEFINITIONS' },
      },
    ];

    // Build dynamic chain links based on role and assigned chainId(s)
    const chainIdRaw = localStorage.getItem('chainId');
    const chainIds: string[] = chainIdRaw
      ? (() => { try { return JSON.parse(chainIdRaw); } catch { return []; } })()
      : [];

    let overviewLink = '/chains';
    let brandingLink = '/chains';
    let catalogLink  = '/chains';
    let loyaltyLink  = '/chains';
    let reportsLink  = '/chains';

    if (this.role === 'chain_manager') {
      overviewLink = '/my-chains';
      if (chainIds && chainIds.length === 1) {
        const cid = chainIds[0];
        overviewLink = `/chain/${cid}/overview`;
        brandingLink = `/chain/${cid}/branding`;
        catalogLink  = `/chain/${cid}/catalog`;
        loyaltyLink  = `/chain/${cid}/loyalty`;
        reportsLink  = `/chain-reports/${cid}`;
      } else {
        brandingLink = '/my-chains';
        catalogLink  = '/my-chains';
        loyaltyLink  = '/my-chains';
        reportsLink  = '/my-chains';
      }
    }

    allItems.push({
      title: t('MENU.CHAINS'),
      icon: 'link-2-outline',
      data: { key: 'CHAINS' },
      children: [
        { title: t('MENU.CHAINS_OVERVIEW'), icon: 'grid-outline',          link: overviewLink },
        { title: t('MENU.CHAINS_REPORTS'),  icon: 'bar-chart-outline',   link: reportsLink },
        { title: t('MENU.CHAINS_BRANDING'), icon: 'color-palette-outline', link: brandingLink },
        { title: t('MENU.CHAINS_CATALOG'),  icon: 'shopping-cart-outline', link: catalogLink  },
        { title: t('MENU.CHAINS_LOYALTY'),  icon: 'award-outline',         link: loyaltyLink  },
      ],
    });

    if (this.role === 'manager') {
      allItems.push({
        title: t('MENU.ECOMMERCE_MANAGEMENT'),
        link: '/manager-ecommerce',
        icon: 'shopping-cart-outline',
        data: { key: 'ECOMMERCE_MANAGEMENT' },
      });

      this.menu = allItems.filter(
        (item) =>
          item.data?.key === 'DASHBOARD' ||
          item.data?.key === 'STORES' ||
          item.data?.key === 'STORE_DOOR_STATUS' ||
          item.data?.key === 'NFC_MONITORING' ||
          item.data?.key === 'MOBILE_DIAGNOSTICS' ||
          item.data?.key === 'GRAPH' ||
          item.data?.key === 'USERS_VISIT' ||
          item.data?.key === 'HOLIDAYS' ||
          item.data?.key === 'CARD_ACCESS' ||
          item.data?.key === 'ECOMMERCE_MANAGEMENT' ||
          item.data?.key === 'DEVELOPER_CLIENTS' ||
          item.data?.key === 'SECURITY_SETTINGS' ||
          item.data?.key === 'SUPPORT_CENTER' ||
          item.data?.key === 'GLOBAL_CATALOG' ||
          item.data?.key === 'INVOICE_IMPORTS_UNASSIGNED'
      );
    } else if (this.role === 'chain_manager') {
      this.menu = allItems.filter(
        (item) =>
          item.data?.key === 'CHAINS' ||
          item.data?.key === 'STORE_DOOR_STATUS' ||
          item.data?.key === 'NFC_MONITORING' ||
          item.data?.key === 'DEVELOPER_CLIENTS' ||
          item.data?.key === 'SECURITY_SETTINGS' ||
          item.data?.key === 'SUPPORT_CENTER' ||
          item.data?.key === 'GLOBAL_CATALOG' ||
          item.data?.key === 'INVOICE_IMPORTS_UNASSIGNED'
      );
    } else if (this.role === 'receipt_auditor') {
      this.menu = [
        {
          title: t('MENU.ORDERS'),
          link: '/auditor-orders',
          icon: 'shopping-cart-outline',
          data: { key: 'ORDERS' },
        },
        {
          title: t('MENU.SUPPORT_CENTER'),
          link: '/support',
          icon: 'question-mark-circle-outline',
          data: { key: 'SUPPORT_CENTER' },
        },
      ];
    } else {
      this.menu = allItems;
    }
  }

  load2FAStatus() {
    this.apiService.getRequest(ApiUrl.twoFaStatus).subscribe((resp: any) => {
      this.show2FAWarning = !resp.response.data.twoFactorEnabled;
    }, () => {
      // Silently ignore — don't block the layout if the status call fails
    });
  }

  loadApiVersion() {
    const versionUrl = this.apiService.imageUrl + ApiUrl.apiVersion;
    this.http.get<any>(versionUrl).subscribe({
      next: (res: any) => {
        this.apiCommit = res?.commit ? String(res.commit).slice(0, 7) : (res?.version || '');
        this.apiBuildDate = res?.buildDate || '';
      },
      error: () => {
        // Silently ignore — version info is optional
      }
    });
  }

  dismissWarning() {
    this.show2FAWarning = false;
  }

  toggle() {
    this.isSidebarOpened = !this.isSidebarOpened;
    this.sidebarService.toggle(false, 'left');
  }

  onLogout() {
    localStorage.clear();
    this.route.navigate(['/login']);
  }
}
